import { useState, useEffect } from "react";
import { getSchedules } from "../../../../services/planningService";
import { formatTimeToHHmm } from "../../../../utils/dateUtils";

export interface PlannerData {
  id?: string;
  day: string;
  startTime: string;
  endTime: string;
  subjectCode?: string; // For students/parents
  teacherName?: string; // For students/parents
  className?: string;   // For teachers
  subjectColor?: string; // For subject color backgrounds
}

interface UseGenericPlannerParams {
  role: "teacher" | "student" | "parent";
  classIds?: string[];   // For students/parents
  teacherIds?: string[]; // For teachers
}

const useGenericPlanner = ({ role, classIds, teacherIds }: UseGenericPlannerParams) => {
  const [data, setData] = useState<PlannerData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Skip API call if no valid IDs are provided
    const hasValidIds = (classIds && classIds.length > 0) || (teacherIds && teacherIds.length > 0);
    if (!hasValidIds) {
      setData([]);
      setIsLoading(false);
      return;
    }
    
    const fetchPlannerData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch data from the planning service
        const schedules = await getSchedules(classIds, teacherIds);
        // Normalize the data
        const normalizedData: PlannerData[] = schedules.map((entry: any) => ({
          day: entry.dayOfTheWeek,
          startTime: formatTimeToHHmm(entry.startTime) || "",
          endTime: formatTimeToHHmm(entry.endTime) || "",
          ...(role === "student" ? {
                subjectCode: entry.classSubject?.subject?.code || '',
                teacherName: `${entry.classSubject?.teacher?.user?.firstName || ''} ${entry.classSubject?.teacher?.user?.lastName || ''}`.trim(),
                subjectColor: entry.classSubject?.subject?.colorCode || undefined,
              }
            : 
            role === "teacher"
            ? { 
                className: entry.classSubject?.class?.name,
                subjectCode: entry.classSubject?.subject?.code || '',
                subjectColor: entry.classSubject?.subject?.colorCode || undefined 
              }
            : {}),
        }));
        setData(normalizedData);

      } catch (err: any) {
        setError(err.message || "An error occurred");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlannerData();
  }, [role, classIds, teacherIds]);

  return { data, isLoading, error };
};

export default useGenericPlanner;
